import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Receipt,
  Search,
  Filter,
  RefreshCw,
  Eye,
  ExternalLink,
  X,
  Calendar,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  CreditCard,
  Layers,
  FileText,
  Landmark
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../context/AuthContext';
import ReversarPagoModal from '../components/ReversarPagoModal';
import { pagosAPI, describirError } from '../services/api';
import { formatoMoneda } from '../utils/finanzas';
import CajaOperativa from '../components/CajaOperativa';
import '../styles/Pages.css';
import '../styles/DataTable.css';
import '../styles/FormModal.css';
import '../styles/Pagos.css';

const moneda = formatoMoneda;

const fechaCorta = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).toUpperCase();
};

const fechaHora = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ESTADOS = [
  { value: '', label: 'Todos los estados' },
  { value: 'APLICADO', label: 'Aplicado' },
  { value: 'REVERSADO', label: 'Reversado' }
];

const FORMAS_PAGO = [
  { value: '', label: 'Todas las formas' },
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'DEPOSITO', label: 'Depósito' },
  { value: 'OTRO', label: 'Otro' }
];

const DESTINOS = [
  { value: '', label: 'Todos los destinos' },
  { value: 'CUOTA', label: 'Cuota' },
  { value: 'MORATORIO', label: 'Moratorio' }
];

const TIPOS_CREDITO = [
  { value: '', label: 'Todos los tipos' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'GRUPAL', label: 'Grupal' },
  { value: 'COLOCADORA', label: 'Colocadora' }
];

function Pagos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const initialTab = searchParams.get('tab') === 'historial' ? 'historial' : 'caja';
  const [vista, setVista] = useState(initialTab);

  const [pagos, setPagos] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estado, setEstado] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [destino, setDestino] = useState('');
  const [tipoCredito, setTipoCredito] = useState('');
  const [agrupacionId, setAgrupacionId] = useState('');
  const [page, setPage] = useState(1);

  // Reversa (P08-B0): solo con PAGOS_REVERSAR; el backend revalida permiso y estado.
  const { hasPermission } = useAuth();
  const puedeReversar = hasPermission('PAGOS_REVERSAR');
  const [pagoAReversar, setPagoAReversar] = useState(null);
  const esReversable = (p) => puedeReversar && p.estado === 'APLICADO' && p.reversible !== false;
  // Aviso visible (no solo ocultar el boton) cuando el usuario podria reversar pero el pago no lo permite.
  const esNoReversible = (p) => puedeReversar && p.estado === 'APLICADO' && p.reversible === false;

  // Detalle seleccionado
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const cargarPagos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await pagosAPI.listarGlobal({
        page,
        pageSize: 25,
        search: busqueda || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        estado: estado || undefined,
        formaPago: formaPago || undefined,
        destino: destino || undefined,
        tipoCredito: tipoCredito || undefined,
        agrupacionId: agrupacionId || undefined
      });
      setPagos(res.data.data);
      setPagination(res.data.pagination);
      setKpis(res.data.kpis);
    } catch (e) {
      const err = describirError(e);
      setError(err);
      toast.error(`Error al cargar pagos: ${err.mensaje}`);
    } finally {
      setCargando(false);
    }
  }, [page, busqueda, fechaDesde, fechaHasta, estado, formaPago, destino, tipoCredito, agrupacionId, toast]);

  useEffect(() => {
    if (vista === 'historial') {
      cargarPagos();
    }
  }, [cargarPagos, vista]);

  const abrirDetalle = async (id) => {
    setCargandoDetalle(true);
    try {
      const res = await pagosAPI.obtener(id);
      setPagoSeleccionado(res.data);
    } catch (e) {
      toast.error('No se pudo obtener el detalle del pago');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFechaDesde('');
    setFechaHasta('');
    setEstado('');
    setFormaPago('');
    setDestino('');
    setTipoCredito('');
    setAgrupacionId('');
    setPage(1);
  };

  return (
    <div className="page-container pagos-page">
      {/* Selector de Vista: Caja Operativa vs Historial Global */}
      <div className="pagos-view-switcher">
        <button
          type="button"
          className={`switcher-btn ${vista === 'caja' ? 'is-active' : ''}`}
          onClick={() => {
            setVista('caja');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'caja');
              return next;
            });
          }}
        >
          <Landmark size={16} />
          <span>Caja / Recibir Pago</span>
        </button>

        <button
          type="button"
          className={`switcher-btn ${vista === 'historial' ? 'is-active' : ''}`}
          onClick={() => {
            setVista('historial');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'historial');
              return next;
            });
          }}
        >
          <Receipt size={16} />
          <span>Historial Global de Pagos</span>
        </button>
      </div>

      {vista === 'caja' ? (
        <CajaOperativa initialCreditFolio={searchParams.get('credito')} />
      ) : (
        <>
      {/* KPIs Banner */}
      {kpis && (
        <div className="kpi-grid pagos-kpis">
          <div className="kpi-card">
            <div className="kpi-icon-wrapper is-primary">
              <Calendar size={20} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Pagos de hoy</span>
              <span className="kpi-value">{kpis.pagosHoyCount}</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper is-success">
              <DollarSign size={20} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Importe recibido hoy</span>
              <span className="kpi-value">{moneda(kpis.importeHoyTotal)}</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper is-info">
              <CheckCircle2 size={20} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Pagos a cuotas</span>
              <span className="kpi-value">{kpis.pagosCuotasCount}</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper is-warning">
              <AlertTriangle size={20} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Pagos a moratorios</span>
              <span className="kpi-value">{kpis.pagosMoratoriosCount}</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper is-danger">
              <RotateCcw size={20} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Pagos reversados</span>
              <span className="kpi-value">{kpis.pagosReversadosCount}</span>
            </div>
          </div>
        </div>
      )}

      {/* Barra de Filtros Rediseñada */}
      <div className="filters-card">
        <div className="filters-header">
          <div className="filters-title">
            <Filter size={16} />
            <span>Filtros y Búsqueda</span>
          </div>
          <div className="filters-actions">
            {(busqueda || fechaDesde || fechaHasta || estado || formaPago || destino || tipoCredito || agrupacionId) && (
              <button type="button" className="btn-text" onClick={limpiarFiltros}>
                Limpiar filtros
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={cargarPagos}
              disabled={cargando}
              title="Recargar datos"
            >
              <RefreshCw size={14} className={cargando ? 'spin' : ''} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>

        <div className="filters-grid">
          {/* Búsqueda y Rango de Fechas */}
          <div className="filter-item filter-search">
            <label className="filter-label" htmlFor="search-input">Buscar</label>
            <div className="search-box">
              <Search className="search-icon" size={16} />
              <input
                id="search-input"
                type="text"
                placeholder="Folio pago, crédito, cliente o referencia..."
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPage(1);
                }}
              />
              {busqueda && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => {
                    setBusqueda('');
                    setPage(1);
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="filter-item filter-fecha-desde">
            <label className="filter-label" htmlFor="fecha-desde">Fecha Desde</label>
            <input
              id="fecha-desde"
              type="date"
              className="filter-input"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="filter-item filter-fecha-hasta">
            <label className="filter-label" htmlFor="fecha-hasta">Fecha Hasta</label>
            <input
              id="fecha-hasta"
              type="date"
              className="filter-input"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* Criterios Operativos */}
          <div className="filter-item filter-estado">
            <label className="filter-label" htmlFor="filtro-estado">Estado</label>
            <select
              id="filtro-estado"
              className="filter-select"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPage(1);
              }}
            >
              {ESTADOS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item filter-forma">
            <label className="filter-label" htmlFor="filtro-forma">Forma de Pago</label>
            <select
              id="filtro-forma"
              className="filter-select"
              value={formaPago}
              onChange={(e) => {
                setFormaPago(e.target.value);
                setPage(1);
              }}
            >
              {FORMAS_PAGO.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item filter-destino">
            <label className="filter-label" htmlFor="filtro-destino">Destino</label>
            <select
              id="filtro-destino"
              className="filter-select"
              value={destino}
              onChange={(e) => {
                setDestino(e.target.value);
                setPage(1);
              }}
            >
              {DESTINOS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item filter-tipo">
            <label className="filter-label" htmlFor="filtro-tipo">Tipo de Crédito</label>
            <select
              id="filtro-tipo"
              className="filter-select"
              value={tipoCredito}
              onChange={(e) => {
                setTipoCredito(e.target.value);
                setPage(1);
              }}
            >
              {TIPOS_CREDITO.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item filter-agrupacion">
            <label className="filter-label" htmlFor="filtro-agrupacion">Agrupación</label>
            <input
              id="filtro-agrupacion"
              type="text"
              className="filter-input"
              placeholder="ID o folio de agrupación..."
              value={agrupacionId}
              onChange={(e) => {
                setAgrupacionId(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      {error ? (
        <div className="error-card">
          <AlertTriangle size={24} className="error-icon" />
          <div>
            <h3>Error al cargar los pagos</h3>
            <p>{error.mensaje}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={cargarPagos}>
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {/* Vista Desktop: Tabla (Máximo 7 Columnas) */}
          <div className="data-table-container desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="col-pago">PAGO</th>
                  <th className="col-cliente-credito">CLIENTE / CRÉDITO</th>
                  <th className="col-tipo-agrupacion">TIPO / AGRUPACIÓN</th>
                  <th className="col-destino">DESTINO</th>
                  <th className="col-importe text-right">IMPORTE</th>
                  <th className="col-forma-estado">FORMA / ESTADO</th>
                  <th className="col-acciones text-right">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {cargando ? (
                  <tr>
                    <td colSpan="7" className="table-loading">
                      <RefreshCw size={20} className="spin" />
                      <span>Cargando pagos...</span>
                    </td>
                  </tr>
                ) : pagos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="table-empty">
                      No se encontraron pagos con los criterios seleccionados.
                    </td>
                  </tr>
                ) : (
                  pagos.map((p) => (
                    <tr key={p.id} className={p.estado === 'REVERSADO' ? 'row-reversado' : ''}>
                      <td className="col-pago">
                        <div className="cell-stack">
                          <span className="font-mono text-bold pago-folio-cell">{p.folioPago}</span>
                          <span className="cell-subtext">{fechaCorta(p.fechaPago)}</span>
                        </div>
                      </td>
                      <td className="col-cliente-credito">
                        <div className="cell-stack">
                          <span className="cliente-nombre-cell" title={p.cliente?.nombreCompleto}>{p.cliente?.nombreCompleto || '—'}</span>
                          <button
                            type="button"
                            className="link-button font-mono credito-folio-cell"
                            onClick={() => navigate(`/creditos/${p.creditoId}`)}
                            title="Ver expediente del crédito"
                          >
                            {p.credito?.folio || '—'}
                          </button>
                        </div>
                      </td>
                      <td className="col-tipo-agrupacion">
                        <div className="cell-stack">
                          <span className="tag-tipo">{p.credito?.tipoCredito || 'PERSONAL'}</span>
                          {p.credito?.agrupacion ? (
                            <span className="agrupacion-badge text-truncate" title={p.credito.agrupacion.nombre}>
                              {p.credito.agrupacion.nombre}
                            </span>
                          ) : (
                            <span className="cell-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="col-destino">
                        <span className={`destino-badge is-${p.destino === 'CUOTA_Y_MORATORIO' ? 'moratorio' : p.destino?.toLowerCase()}`}>
                          {p.destino === 'CUOTA_Y_MORATORIO' ? 'CUOTA + MORATORIO' : p.destino}
                        </span>
                      </td>
                      <td className="col-importe text-right font-bold text-success">
                        {moneda(p.importeRecibido)}
                      </td>
                      <td className="col-forma-estado">
                        <div className="cell-stack">
                          <span className="forma-pago-text">{p.formaPago}</span>
                          <StatusBadge value={p.estado} />
                        </div>
                      </td>
                      <td className="col-acciones text-right">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => abrirDetalle(p.id)}
                          title="Ver detalle del pago"
                        >
                          <Eye size={16} />
                        </button>
                        {esReversable(p) && (
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => setPagoAReversar(p)}
                            title="Reversar pago"
                            aria-label={`Reversar pago ${p.folioPago}`}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                        {esNoReversible(p) && (
                          <span className="cell-muted" data-testid="pago-no-reversible" title="Pago histórico migrado o incluido en un cierre de caja cerrado: no se puede reversar." style={{ fontSize: '0.75rem', marginLeft: 6 }}>
                            No reversible
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Vista Mobile & Tablet (< 1200px): Cards */}
          <div className="mobile-only pagos-cards">
            {cargando ? (
              <div className="mobile-loading">
                <RefreshCw size={20} className="spin" />
                <span>Cargando pagos...</span>
              </div>
            ) : pagos.length === 0 ? (
              <div className="mobile-empty">
                No se encontraron pagos con los criterios seleccionados.
              </div>
            ) : (
              pagos.map((p) => (
                <div key={p.id} className={`pago-card ${p.estado === 'REVERSADO' ? 'is-reversado' : ''}`}>
                  <div className="pago-card-header">
                    <div className="pago-card-identity">
                      <span className="pago-card-folio font-mono text-bold">{p.folioPago}</span>
                      <span className="pago-card-cliente font-medium">{p.cliente?.nombreCompleto || '—'}</span>
                      <span className="pago-card-credito font-mono">{p.credito?.folio || '—'}</span>
                    </div>
                    <StatusBadge value={p.estado} />
                  </div>

                  <div className="pago-card-body">
                    <div className="pago-card-row">
                      <span className="pago-card-val text-bold text-success pago-card-monto">{moneda(p.importeRecibido)}</span>
                      <span className={`destino-badge is-${p.destino === 'CUOTA_Y_MORATORIO' ? 'moratorio' : p.destino?.toLowerCase()}`}>{p.destino === 'CUOTA_Y_MORATORIO' ? 'CUOTA + MORATORIO' : p.destino}</span>
                    </div>
                    <div className="pago-card-row pago-card-meta">
                      <span>{p.formaPago}</span>
                      <span>·</span>
                      <span>{fechaCorta(p.fechaPago)}</span>
                    </div>
                  </div>

                  <div className="pago-card-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => abrirDetalle(p.id)}
                    >
                      <Eye size={14} />
                      <span>Ver</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/creditos/${p.creditoId}`)}
                    >
                      <ExternalLink size={14} />
                      <span>Ir al crédito</span>
                    </button>
                    {esReversable(p) && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setPagoAReversar(p)}
                      >
                        <RotateCcw size={14} />
                        <span>Reversar pago</span>
                      </button>
                    )}
                    {esNoReversible(p) && (
                      <span className="pago-card-meta" data-testid="pago-no-reversible" title="Pago histórico migrado o incluido en un cierre de caja cerrado: no se puede reversar.">
                        No reversible: pago histórico migrado o en un cierre cerrado.
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Paginación */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Mostrando página {pagination.page} de {pagination.totalPages} ({pagination.total} pagos en total)
              </span>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {pagoAReversar && (
        <ReversarPagoModal
          pago={pagoAReversar}
          onClose={() => setPagoAReversar(null)}
          onReversado={async () => {
            setPagoAReversar(null);
            await cargarPagos();
            toast.exito('Pago reversado. Permanece en el historial como REVERSADO.');
          }}
        />
      )}

      {/* Drawer / Modal de Detalle de Pago */}
      {(pagoSeleccionado || cargandoDetalle) && (
        <div className="modal-overlay" onClick={() => setPagoSeleccionado(null)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-title">
                <Receipt size={20} />
                <h2>Detalle del Pago {pagoSeleccionado?.folioPago}</h2>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setPagoSeleccionado(null)}
                aria-label="Cerrar modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {cargandoDetalle ? (
                <div className="modal-loading">
                  <RefreshCw size={24} className="spin" />
                  <span>Cargando detalle...</span>
                </div>
              ) : pagoSeleccionado ? (
                <div className="pago-detalle-container">
                  {/* Resumen principal */}
                  <div className="detalle-banner">
                    <div className="banner-item">
                      <span className="banner-label">Importe Recibido</span>
                      <span className="banner-value text-success">{moneda(pagoSeleccionado.importeRecibido)}</span>
                    </div>
                    <div className="banner-item">
                      <span className="banner-label">Forma de Pago</span>
                      <span className="banner-value">{pagoSeleccionado.formaPago}</span>
                    </div>
                    <div className="banner-item">
                      <span className="banner-label">Destino</span>
                      <span className={`destino-badge is-${pagoSeleccionado.destino.toLowerCase()}`}>
                        {pagoSeleccionado.destino}
                      </span>
                    </div>
                    <div className="banner-item">
                      <span className="banner-label">Estado</span>
                      <StatusBadge value={pagoSeleccionado.estado} />
                    </div>
                  </div>

                  {/* Bloque Informativo */}
                  <div className="detalle-sections">
                    <div className="detalle-section">
                      <h3><FileText size={16} /> Información General</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Folio Pago</span>
                          <span className="info-val font-mono">{pagoSeleccionado.folioPago}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Fecha Pago (Cliente)</span>
                          <span className="info-val">{fechaCorta(pagoSeleccionado.fechaPago)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Fecha Captura (Sistema)</span>
                          <span className="info-val">{fechaHora(pagoSeleccionado.fechaRegistro)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Registrado Por</span>
                          <span className="info-val">{pagoSeleccionado.registradoPor}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Referencia</span>
                          <span className="info-val">{pagoSeleccionado.referencia || '—'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Observaciones</span>
                          <span className="info-val">{pagoSeleccionado.observaciones || '—'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="detalle-section">
                      <h3><CreditCard size={16} /> Crédito y Cliente</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Folio Crédito</span>
                          <span className="info-val font-mono">{pagoSeleccionado.credito?.folio || '—'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Tipo Crédito</span>
                          <span className="info-val">{pagoSeleccionado.credito?.tipoCredito || '—'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Cliente Titular</span>
                          <span className="info-val font-medium">{pagoSeleccionado.cliente?.nombreCompleto || '—'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Folio Cliente</span>
                          <span className="info-val font-mono">{pagoSeleccionado.cliente?.folio || '—'}</span>
                        </div>
                        {pagoSeleccionado.credito?.agrupacion && (
                          <div className="info-item full-width">
                            <span className="info-label">Agrupación</span>
                            <span className="info-val">
                              {pagoSeleccionado.credito.agrupacion.folio} - {pagoSeleccionado.credito.agrupacion.nombre}
                            </span>
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setPagoSeleccionado(null);
                            navigate(`/creditos/${pagoSeleccionado.creditoId}`);
                          }}
                        >
                          <ExternalLink size={14} />
                          <span>Ir al expediente del crédito</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Reversa (si aplica) */}
                  {pagoSeleccionado.reversa && (
                    <div className="reversa-alert-box">
                      <RotateCcw size={18} className="alert-icon" />
                      <div>
                        <h4 className="alert-title">Pago Reversado</h4>
                        <p className="alert-desc">
                          Este pago fue anulado el {fechaHora(pagoSeleccionado.reversa.createdAt)} por{' '}
                          <strong>{pagoSeleccionado.reversa.reversadoPor}</strong>.
                        </p>
                        <p className="alert-reason">
                          <strong>Motivo:</strong> {pagoSeleccionado.reversa.motivo}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Desglose de Aplicaciones */}
                  <div className="detalle-section">
                    <h3><Layers size={16} /> Aplicaciones del Pago ({pagoSeleccionado.aplicaciones?.length || 0})</h3>
                    <table className="data-table small-table">
                      <thead>
                        <tr>
                          <th>ORDEN</th>
                          <th>TIPO DESTINO</th>
                          <th>CUOTA / CARGO</th>
                          <th>VENCIMIENTO</th>
                          <th className="text-right">IMPORTE APLICADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagoSeleccionado.aplicaciones && pagoSeleccionado.aplicaciones.length > 0 ? (
                          pagoSeleccionado.aplicaciones.map((a) => (
                            <tr key={a.id}>
                              <td>#{a.ordenAplicacion}</td>
                              <td>
                                <span className={`destino-badge is-${a.tipo.toLowerCase()}`}>
                                  {a.tipo}
                                </span>
                              </td>
                              <td>
                                {a.tipo === 'CUOTA'
                                  ? `Cuota #${a.numeroPago}`
                                  : `Cargo Moratorio (Cuota #${a.numeroPago})`}
                              </td>
                              <td>{fechaCorta(a.fechaVencimiento)}</td>
                              <td className="text-right font-mono text-bold">
                                {moneda(a.importeAplicado)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="table-empty">Sin aplicaciones registradas.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPagoSeleccionado(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default Pagos;
